/**
 * @author Ryan Gladstone
 * Webpack migration by Umer Hassan
 */

import angular from 'angular';

const statsService = (
  $http,
  $q,
  $translate,
  storyProcessingService,
  timezoneService
) => {
  const SECONDS_TO_SUBTRACT_FOR_CLIENT = 600;

  statsService.defaultGoals = [
    {
      startHour: 0,
      endHour: 6,
      label: 'Overnight',
      goalPercentage: 5
    },
    {
      startHour: 6,
      endHour: 9,
      label: 'Early',
      goalPercentage: 15
    },
    {
      startHour: 9,
      endHour: 12,
      label: 'Morning',
      goalPercentage: 30
    },
    {
      startHour: 12,
      endHour: 15,
      label: 'Midday',
      goalPercentage: 25
    },
    {
      startHour: 15,
      endHour: 18,
      label: 'Afternoon',
      goalPercentage: 15
    },
    {
      startHour: 18,
      endHour: 21,
      label: 'Evening',
      goalPercentage: 8
    },
    {
      startHour: 21,
      endHour: 24,
      label: 'Late',
      goalPercentage: 2
    }
  ];

  statsService.getBreakdown = (
    type,
    startTime,
    endTime,
    sections,
    platforms,
    pitchStatus,
    sourceType
  ) => {
    const deferred = $q.defer();

    let endpoint;
    if (type === 'pitching') {
      endpoint = 'pitchedCount';
    } else if (type === 'accuracy') {
      endpoint = 'plannedTimeAccuracy';
    }

    if (type === 'accuracy') {
      deferred.resolve([
        {
          label: $translate.instant('ON_TIME').toLowerCase(),
          count: 0
        },
        {
          label: $translate.instant('LATE').toLowerCase(),
          count: 0
        },
        {
          label: $translate.instant('NO_PLANNED_TIME').toLowerCase(),
          count: 0
        }
      ]);
    } else {
      $http({
        method: 'GET',
        url: `/editorial-search/websked-stats/${endpoint}`,
        params: {
          startTime: startTime - SECONDS_TO_SUBTRACT_FOR_CLIENT,
          endTime: endTime - SECONDS_TO_SUBTRACT_FOR_CLIENT,
          sections,
          platforms,
          pitchStatus,
          sourceType
        }
      })
        .success((data) => {
          const breakdown = [];

          if (type === 'pitching') {
            breakdown.push({
              label: $translate.instant('PITCHED_M_PL'),
              count: data.numPitched
            });
            breakdown.push({
              label: $translate.instant('NOT_PITCHED'),
              count: data.numTotal - data.numPitched
            });
          } else if (type === 'accuracy') {
            breakdown.push({
              label: $translate.instant('ON_TIME').toLowerCase(),
              count: data.numWithAccuratePlannedTime
            });
            breakdown.push({
              label: $translate.instant('LATE').toLowerCase(),
              count: data.numWithInaccuratePlannedTime
            });
            breakdown.push({
              label: $translate.instant('NO_PLANNED_TIME').toLowerCase(),
              count: data.numWithoutPlannedTime
            });
          }

          deferred.resolve(breakdown);
        })
        .error((data) => {
          deferred.reject(data);
        });
    }

    return deferred.promise;
  };

  statsService.getPitchAcceptanceRate = (startTime, endTime, sections, platforms) => {
    const deferred = $q.defer();

    $http({
      method: 'GET',
      url: '/editorial-search/websked-stats/pitchResponse',
      params: {
        startTime: startTime - SECONDS_TO_SUBTRACT_FOR_CLIENT,
        endTime: endTime - SECONDS_TO_SUBTRACT_FOR_CLIENT,
        sections,
        platforms
      }
    })
      .success((data) => {
        if (data && Array.isArray(data)) {
          let accepted = 0;
          let totalResponses = 0;

          angular.forEach(data, (pc) => {
            const status = pc.key;
            if (
              status === 'watching' ||
                  status === 'saving' ||
                  status === 'used'
            ) {
              accepted += pc.count;
              totalResponses += pc.count;
              // only need this branch if you ignore 'pitched'
              // } else if (status === 'not watching') {
              //   totalResponses += pc.count;
            } else {
              totalResponses += pc.count;
            }
          });

          const percentAcceptanceRate = Math.floor(
            (accepted / totalResponses) * 100
          );

          deferred.resolve(percentAcceptanceRate);
        } else {
          deferred.resolve(Number.NaN);
        }
      })
      .error((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  };

  statsService.getMedianDeadlineMiss = () => {
    const deferred = $q.defer();
    deferred.resolve(Number.NaN);
    return deferred.promise;
  };

  statsService.getStoryCountsByCurationStatus = (
    startTime,
    endTime,
    interval,
    platforms,
    sourceType
  ) => {
    const deferred = $q.defer();

    const pitchStates = {
      used: [],
      watching: [],
      'not watching': [],
      pitched: [],
      saving: [],
      withdrawn: []
    };

    const countData = [
      {
        key: 'pitched',
        values: []
      },
      {
        key: 'withdrawn',
        values: []
      },
      {
        key: 'not watching',
        values: []
      },
      {
        key: 'watching',
        values: []
      },
      {
        key: 'saving',
        values: []
      },
      {
        key: 'used',
        values: []
      }
    ];

    $http({
      method: 'GET',
      url: '/editorial-search/websked-stats/storyCount',
      params: {
        startTime,
        endTime,
        platforms,
        interval,
        offset: timezoneService.userStatsOffset,
        // diff
        includePitchStatusCounts: true,
        sourceType
      }
    })
      .success((data) => {
        angular.forEach(data, (stat) => {
          const currentStat = {
            date: null,
            pitchStateAdded: {}
          };
          angular.forEach(stat.counts.byPitchStatus, (pitchStatus) => {
            currentStat.date = stat.date * 1000;
            pitchStates[pitchStatus.key].push([
              currentStat.date,
              pitchStatus.count
            ]);
            currentStat.pitchStateAdded[pitchStatus.key] = true;
          });

          // loop through states, see if it is already added
          angular.forEach(countData, (pState) => {
            if (currentStat.pitchStateAdded[pState.key] !== true) {
              pitchStates[pState.key].push([currentStat.date, 0]);
            }
          });
        });

        // tranform to the array that the directive is expecting
        let pitchedPlacement = null;
        angular.forEach(countData, (sdValue, sdIndex) => {
          countData[sdIndex].values = pitchStates[sdValue.key];
          if (sdValue.key === 'pitched') {
            pitchedPlacement = sdIndex;
          }
        });

        // rename all keys to allow translation
        countData.unshift(
          {
            key: $translate.instant('IGNORED'),
            values: countData[pitchedPlacement].values
          },
          {
            key: $translate.instant('WITHDRAWN'),
            values: countData[pitchedPlacement + 1].values
          },
          {
            key: $translate.instant('NOT_WATCHING').toLowerCase(),
            values: countData[pitchedPlacement + 2].values
          },
          {
            key: $translate.instant('WATCHING').toLowerCase(),
            values: countData[pitchedPlacement + 3].values
          },
          {
            key: $translate.instant('SAVING').toLowerCase(),
            values: countData[pitchedPlacement + 4].values
          },
          {
            key: $translate.instant('USED').toLowerCase(),
            values: countData[pitchedPlacement + 5].values
          }
        );
        countData.splice(pitchedPlacement + 6, 6);

        deferred.resolve(countData);
      })
      .error((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  };

  statsService.getStoryCountsByPitchStatus = (
    startTime,
    endTime,
    interval,
    sections,
    sourceType
  ) => {
    const deferred = $q.defer();

    const pitchStates = {
      'not pitched': [],
      pitched: []
    };

    const countData = [
      {
        key: 'not pitched',
        values: []
      },
      {
        key: 'pitched',
        values: []
      }
    ];

    $http({
      method: 'GET',
      url: '/editorial-search/websked-stats/storyCount',
      params: {
        startTime,
        endTime,
        sections,
        interval,
        offset: timezoneService.userStatsOffset,
        // different than getStoryCountsByCurationStatus()
        includePitchedCount: true,
        sourceType
      }
    })
      .success((data) => {
        angular.forEach(data, (stat) => {
          pitchStates['not pitched'].push([
            stat.date * 1000,
            stat.counts.total - stat.counts.numPitched
          ]);
          pitchStates.pitched.push([
            stat.date * 1000,
            stat.counts.numPitched
          ]);
        });
        // tranform to the array that the directive is expecting
        let pitchedPlacement = null;
        angular.forEach(countData, (sdValue, sdIndex) => {
          countData[sdIndex].values = pitchStates[sdValue.key];
          if (sdValue.key === 'not pitched') {
            pitchedPlacement = sdIndex;
          }
        });

        // rename all keys to allow translation
        countData.unshift(
          {
            key: $translate.instant('NOT_PITCHED').toLowerCase(),
            values: countData[pitchedPlacement].values
          },
          {
            key: $translate.instant('PITCHED_M_PL').toLowerCase(),
            values: countData[pitchedPlacement + 1].values
          }
        );
        countData.splice(pitchedPlacement + 2, 2);

        deferred.resolve(countData);
      })
      .error((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  };

  statsService.getStoryCounts = (
    startTime,
    endTime,
    interval,
    sections,
    platforms,
    pitchStatus,
    sourceType
  ) => {
    const deferred = $q.defer();

    $http({
      method: 'GET',
      url: '/editorial-search/websked-stats/storyCount',
      params: {
        sections,
        platforms,
        startTime,
        endTime,
        interval,
        offset: timezoneService.userStatsOffset,
        pitchStatus,
        sourceType
      }
    })
      .success((data) => {
        const d3Data = [];
        angular.forEach(data, (stat) => {
          d3Data.push([stat.date * 1000, stat.counts.total]);
        });

        deferred.resolve(d3Data);
      })
      .error((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  };

  statsService.getStoryCountsByLiveStatus = (
    startTime,
    endTime,
    interval,
    liveStatus,
    sections,
    platforms,
    pitchStatus,
    sourceType
  ) => {
    // Call the method with liveStatus as true if you want count of live stories,
    // for not live call with false

    const deferred = $q.defer();

    const params = {
      startTime,
      endTime,
      interval,
      offset: timezoneService.userStatsOffset,
      // Different than getStoryCountsByCurationStatus()
      includeLiveStatusCounts: true,
      sections,
      platforms,
      pitchStatus,
      sourceType
    };

    $http({
      method: 'GET',
      url: '/editorial-search/websked-stats/storyCount',
      params
    })
      .success((data) => {
        const d3DataByLiveStatus = [];

        // looping over the result received
        angular.forEach(data, (stat) => {
          // looping over byLiveStatus objects (i.e. true and false)
          angular.forEach(stat.counts.byLiveStatus, (status) => {
            if (status.key === liveStatus) {
              d3DataByLiveStatus.push([stat.date * 1000, status.count]);
            }
          });
        });
        deferred.resolve(d3DataByLiveStatus);
      })
      .error((data) => {
        deferred.reject(data);
      });

    return deferred.promise;
  };

  statsService.getDefaultGoals = () => {
    const deferred = $q.defer();

    $http({
      method: 'GET',
      url: 'api/stats/goals/default'
    })
      .success((data) => {
        deferred.resolve(data);
      })
      .error((response) => {
        deferred.reject(response);
      });

    return deferred.promise;
  };

  statsService.updateDefaultGoals = (goalsData) => {
    const deferred = $q.defer();

    $http({
      method: 'PUT',
      url: 'api/stats/goals/default',
      data: goalsData
    })
      .success((data) => {
        deferred.resolve(data);
      })
      .error((response) => {
        deferred.reject(response);
      });

    return deferred.promise;
  };

  statsService.getDefaultResetGoals = () => $q.when(statsService.defaultGoals);

  return statsService;
};

const service = ['$http',
  '$q',
  '$translate',
  'storyProcessingService',
  'timezoneService',
  '_',
  statsService
];

export default (ngModule) => {
  ngModule.service('statsService', service);
};
